import { useEffect, useMemo, useRef, useState } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { SkeletonUtils } from "three-stdlib";
import { addEventListener } from "../src/events/eventBus";
import { useFrame } from "@react-three/fiber";

const CONFETTI_CONFIGS = [
  { position: [4.67, 2, 44], delay: 0 },
  { position: [13.43, 2, 50], delay: 200 },
  { position: [-4.17, 2, 50], delay: 200 },
];

export default function Confetti({
  rotation = [0, 0, 0],
  scale = 1,
}) {
  const group = useRef();
  const { scene, animations } = useGLTF("/confetti.glb");
  const clonedScenes = useMemo(
    () =>
      CONFETTI_CONFIGS.map(({ position: [x, y, z] }) => {
        const clone = SkeletonUtils.clone(scene);
        clone.position.set(x, y, z);
        return clone;
      }),
    [scene]
  );
  const [visible, setVisible] = useState(false);
  const [activeIndices, setActiveIndices] = useState([]);
  const actionRefs = useRef([]);
  const mixerRefs = useRef([]);
  const finishedCountRef = useRef(0);
  const repeatTimerRef = useRef(null);
  const playbackIndexRef = useRef(0);
  const startTimeoutsRef = useRef([]);

  useEffect(() => {
    clonedScenes.forEach((clonedScene) => {
      clonedScene.traverse((child) => {
        if (child.isMesh || child.isSkinnedMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
    });
  }, [clonedScenes]);

  useEffect(() => {
    if (!animations || animations.length === 0) return undefined;

    const clip = animations[0];
    if (!clip) return undefined;

    mixerRefs.current = [];
    actionRefs.current = clonedScenes.map((sceneClone, index) => {
      const mixer = new THREE.AnimationMixer(sceneClone);
      const action = mixer.clipAction(clip);
      action.clampWhenFinished = true;
      action.enabled = true;
      mixerRefs.current[index] = mixer;
      return action;
    });

    return () => {
      actionRefs.current.forEach((action) => action?.stop());
      mixerRefs.current.forEach((mixer) => mixer?.stopAllAction());
      actionRefs.current = [];
      mixerRefs.current = [];
    };
  }, [animations, clonedScenes]);

  useEffect(() => {
    if (!group.current) return;
    group.current.rotation.set(rotation[0], rotation[1], rotation[2]);
  }, [rotation]);

  useEffect(() => {
    clonedScenes.forEach((clonedScene) => {
      clonedScene.scale.setScalar(scale);
    });
  }, [clonedScenes, scale]);

  useEffect(() => {
    const unsubscribe = addEventListener("cakeLiftComplete", () => {
      if (actionRefs.current.length === 0) return;

      setActiveIndices([]);
      setVisible(true);
      playbackIndexRef.current = 0;
      finishedCountRef.current = 0;

      if (repeatTimerRef.current) {
        clearTimeout(repeatTimerRef.current);
        repeatTimerRef.current = null;
      }

      const playOnce = () => {
        const mixers = mixerRefs.current;
        const actions = actionRefs.current;
        if (!mixers.length || mixers.length !== actions.length) return;

        startTimeoutsRef.current.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
        startTimeoutsRef.current = [];
        finishedCountRef.current = 0;

        const handleFinished = (event) => {
          if (!actions.includes(event.action)) return;

          finishedCountRef.current += 1;
          if (finishedCountRef.current < actions.length) {
            return;
          }

          mixers.forEach((mixer) => mixer.removeEventListener("finished", handleFinished));

          playbackIndexRef.current += 1;
          if (playbackIndexRef.current >= 3) {
            repeatTimerRef.current = window.setTimeout(() => {
              setVisible(false);
            }, 150);
            return;
          }

          repeatTimerRef.current = window.setTimeout(() => {
            setActiveIndices([]);
            playOnce();
          }, 150);
        };

        mixers.forEach((mixer, index) => {
          const currentAction = actions[index];
          if (!currentAction) return;
          mixer.addEventListener("finished", handleFinished);

          const delay = CONFETTI_CONFIGS[index]?.delay ?? 0;
          const timeoutId = window.setTimeout(() => {
            setActiveIndices((prev) => {
              if (prev.includes(index)) return prev;
              return [...prev, index];
            });
            currentAction.reset().setLoop(THREE.LoopOnce, 1).play();
          }, delay);

          startTimeoutsRef.current.push(timeoutId);
        });
      };

      playOnce();
    });

    return () => {
      unsubscribe();
      if (repeatTimerRef.current) {
        clearTimeout(repeatTimerRef.current);
        repeatTimerRef.current = null;
      }
      startTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      startTimeoutsRef.current = [];
      setActiveIndices([]);
      setVisible(false);
    };
  }, []);

  useFrame((_, delta) => {
    mixerRefs.current.forEach((mixer) => {
      if (mixer) {
        mixer.update(delta);
      }
    });
  });

  return (
    <group ref={group} rotation={rotation} visible={visible}>
      {clonedScenes.map((clonedScene, index) => (
        <primitive
          key={index}
          object={clonedScene}
          visible={activeIndices.includes(index)}
        />
      ))}
    </group>
  );
}

useGLTF.preload("/confetti.glb");
